import React, { useEffect, useState } from "react";
import { X, ExternalLink, ShoppingBag } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { imageUrl } from "@/lib/api";
import { hasMobileOverride } from "@/lib/bannerRenderer";

/**
 * PromoPopup — auto-show popup banner dari /api/popups/active?location=...
 * Props:
 *  - location: "homepage" | "after_login" | "checkout"
 *
 * Gambar: SATU image_url dirender responsif via CSS (kontainer 4:5 di mobile, 16:9 ≥sm, object-cover)
 * mengikuti pola Banner Utama — tanpa pemilihan gambar berbasis window.innerWidth.
 * Kompatibilitas: record lama dengan image_url_mobile berbeda → override CSS (sm:hidden / hidden sm:block).
 *
 * Hidden saat:
 *  - rute /admin/*
 *  - rute /pesanan/* (proses pembayaran / checkout final)
 *  - rute /login, /register, /forgot-password, /reset-password (auth pages)
 */
const HIDDEN_PREFIXES = ["/admin", "/pesanan", "/login", "/register", "/forgot-password", "/reset-password"];
const IMG_CLS = "absolute inset-0 w-full h-full object-cover object-center";

export function PopupImage({ popup }) {
  if (!popup.image_url) return null;
  const legacy = hasMobileOverride({ image_url: popup.image_url, mobile_image_url: popup.image_url_mobile });
  return (
    <div className="relative w-full aspect-[4/5] sm:aspect-[16/9] overflow-hidden bg-[#FAF9F6]" data-testid="popup-image-frame" data-legacy-mobile={legacy ? "1" : "0"}>
      {legacy ? (
        <>
          <img src={imageUrl(popup.image_url_mobile)} alt={popup.title} className={`sm:hidden ${IMG_CLS}`} data-testid="popup-image-mobile" />
          <img src={imageUrl(popup.image_url)} alt={popup.title} className={`hidden sm:block ${IMG_CLS}`} data-testid="popup-image-desktop" />
        </>
      ) : (
        <img src={imageUrl(popup.image_url)} alt={popup.title} className={IMG_CLS} data-testid="popup-image" />
      )}
    </div>
  );
}

export default function PromoPopup({ location = "homepage" }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [popup, setPopup] = useState(null);
  const [closed, setClosed] = useState(false);

  const blocked = HIDDEN_PREFIXES.some((p) => pathname.startsWith(p));

  useEffect(() => {
    if (blocked) return;
    let alive = true;
    api.get(`/popups/active?location=${location}`)
      .then((r) => { if (alive && r.data && r.data.id) setPopup(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [location, pathname, blocked]);

  if (!popup || closed || blocked) return null;

  const markSeen = () => {
    api.post(`/popups/${popup.id}/seen`).catch(() => {});
  };
  const close = () => {
    setClosed(true);
    markSeen();
  };
  const onCta = () => {
    markSeen();
    setClosed(true);
    if (popup.cta_link) {
      if (popup.cta_link.startsWith("http")) {
        window.open(popup.cta_link, "_blank", "noopener,noreferrer");
      } else {
        navigate(popup.cta_link);
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 animate-fadeIn"
      data-testid="promo-popup"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="promo-popup-card">
        <button
          onClick={close}
          className="absolute top-2 right-2 z-10 w-8 h-8 grid place-items-center rounded-full bg-white/90 hover:bg-white shadow"
          data-testid="popup-close-btn"
          aria-label="Tutup"
        >
          <X size={18} />
        </button>
        <PopupImage popup={popup} />
        <div className="p-5 space-y-3">
          <h3 className="font-display text-xl font-black" data-testid="popup-title">{popup.title}</h3>
          {popup.description && <p className="text-sm text-[color:var(--color-text-secondary)] whitespace-pre-line">{popup.description}</p>}
          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <button
              onClick={onCta}
              className="color-btn-primary flex-1 font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2"
              data-testid="popup-cta-btn"
            >
              <ShoppingBag size={16} />
              {popup.cta_text || "Belanja Sekarang"}
              {popup.cta_link && popup.cta_link.startsWith("http") && <ExternalLink size={14} />}
            </button>
            <button
              onClick={close}
              className="sm:flex-none color-btn-secondary font-semibold py-3 px-5 rounded-xl"
              data-testid="popup-close-secondary-btn"
            >
              Tutup
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
